import { reactive } from 'vue';

/**
 * BindingEditorState
 *
 * Transient, per-binding UI state for the binding editor components —
 * never part of AutomationBinding, never serialized, never generated,
 * never exported to YAML or project files. AutomationBinding stays the
 * one persisted/serialized/exported/generated model; this is everything
 * else the editor needs to remember while a binding is being edited.
 *
 * One instance exists per binding row (created and cached by
 * StateBindingsTable.vue, keyed by binding.id) and is passed as a plain
 * prop — alongside the AutomationBinding itself — to every editor
 * component that renders for that binding (BindingEditorRouter.vue and
 * everything it resolves). This lets sibling components (preview,
 * validation, future additions) share the same reactive state instead of
 * each holding its own hidden local state that the others can't see.
 *
 * Current fields:
 *   previewValue         — BindingPreview.vue's test-value input
 *   interpolationEnabled — MappingBindingEditor.vue's interpolation toggle
 *
 * NOTE: the value-transform pipeline (TransformEditor.vue) does NOT live
 * here — it turned out to change the value entering strategy evaluation,
 * so it belongs to the persisted AutomationBinding model (`binding.transforms`)
 * instead, the same as `rules[]`. It briefly lived on this object; see
 * "Architecture Correction — Move Value Transforms into AutomationBinding".
 * This is the boundary test for anything considered for a field here: if
 * preview/validation/generators/export would all need to agree on it,
 * it's not transient UI state — it belongs on AutomationBinding.
 *
 * Future editor-only settings (selected rule, expanded sections, etc.)
 * belong here too — add a field when a feature actually needs one.
 *
 * @returns {object} a reactive editor-state instance
 */
export function createBindingEditorState() {
  return reactive({
    previewValue: '',
    interpolationEnabled: false,
  });
}
